/**
 * Shared state for risk-tiered inline citations.
 *
 * Inline markers live inside `v-html` content, so interactions are wired via
 * event delegation in `MessageBubble` and coordinated through this singleton:
 *   - `activeCitations`  — the list shown in the reference pane.
 *   - `focusRequest`     — a marker → reference-entry scroll/highlight request.
 *   - `hover`            — the single shared hovercard's state.
 */

import { ref } from 'vue'
import type { Citation } from '@/types'

export interface HoverState {
  citation: Citation
  /** Anchor rect of the marker (viewport coords) for positioning. */
  anchor: { top: number; bottom: number; left: number; right: number }
  /** Pinned cards (touch / click) stay open until dismissed. */
  pinned: boolean
}

export interface FocusRequest {
  id: number
  /** Monotonic token so repeat clicks on the same marker re-trigger watchers. */
  token: number
}

const activeCitations = ref<Citation[]>([])
const focusRequest = ref<FocusRequest | null>(null)
const hover = ref<HoverState | null>(null)
/** Whether the desktop references pane is open (user can close it). */
const isPanelOpen = ref(true)
let focusToken = 0
let closeTimer: ReturnType<typeof setTimeout> | null = null

export function useCitations() {
  function setActiveCitations(list: Citation[]): void {
    activeCitations.value = list
    // A fresh reference set (new answer or a clicked bubble) re-opens the pane
    // if the user had closed it.
    if (list.length) isPanelOpen.value = true
  }

  function closePanel(): void {
    isPanelOpen.value = false
  }

  /** Request the reference pane scroll to + highlight citation `id`. */
  function focusCitation(id: number, list?: Citation[]): void {
    if (list) activeCitations.value = list
    isPanelOpen.value = true
    focusRequest.value = { id, token: ++focusToken }
  }

  function cancelClose(): void {
    if (closeTimer) {
      clearTimeout(closeTimer)
      closeTimer = null
    }
  }

  function openHover(state: Omit<HoverState, 'pinned'> & { pinned?: boolean }): void {
    cancelClose()
    hover.value = { pinned: false, ...state }
  }

  function pinHover(): void {
    if (hover.value) hover.value = { ...hover.value, pinned: true }
  }

  /** Close after a short delay so the pointer can travel marker → card. */
  function scheduleClose(delay = 140): void {
    cancelClose()
    closeTimer = setTimeout(() => {
      if (!hover.value?.pinned) hover.value = null
      closeTimer = null
    }, delay)
  }

  /** Force-close immediately (e.g. explicit dismiss). */
  function dismissHover(): void {
    cancelClose()
    hover.value = null
  }

  return {
    activeCitations,
    focusRequest,
    hover,
    isPanelOpen,
    setActiveCitations,
    closePanel,
    focusCitation,
    openHover,
    pinHover,
    scheduleClose,
    cancelClose,
    dismissHover,
  }
}
